import React, { useState } from 'react';
import { DollarSignIcon, TrendingUpIcon, ShieldIcon, GlobeIcon, BarChartIcon, BuildingIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
const StrategySection = () => {
  // State to track which strategy card is expanded
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  // Enhanced strategy data with more detailed information
  const strategies = [{
    icon: <ChevronDownIcon className="w-8 h-8 text-gold-500" data-id="element-271" />,
    title: 'INFASTRUCTURE',
    description: 'High Performance Personal and Professional Coaching & Community',
    benefits: ['Access Business and Life Coaching for mental clarity and focus', 'Personal training to improve physical health and energy', 'Marketing and Branding for Business Exposure', 'Premium Personalized Brand Gifts'],
    example: "Through guided tools and resources, you'll create a strong foundation that elevates both your personal and business brand.",
    metric: {
      value: 'Stronger performance in life and business.',
      label: ''
    },
    testimonial: {
      quote: 'The infrastructure coaching transformed my approach to both business and personal development.',
      author: 'Michael R., Business Owner'
    },
    variant: 'gold'
  }, {
    icon: <DollarSignIcon className="w-8 h-8 text-gold-500" data-id="element-272" />,
    title: 'FUNDING',
    description: 'Accredited Investor Access',
    benefits: ['Access funding options with favorable terms and lower barriers', 'Leveraged strategic partnerships with funding institutions'],
    example: 'Our clients typically secure 30-50% more funding with our accredited investor network than through traditional channels.',
    metric: {
      value: '2.4x',
      label: 'Average funding increase'
    },
    testimonial: {
      quote: "Legacy Lab's funding network opened doors I didn't even know existed.",
      author: 'Lawerence E., Real Estate Investor'
    },
    variant: 'gold'
  }, {
    icon: <TrendingUpIcon className="w-8 h-8 text-gold-500" data-id="element-273" />,
    title: 'CAPITAL ACCELERATION',
    description: 'Private Equity Optimization',
    benefits: ['Maximize cash flow through strategic liquidity management', 'Create passive income streams through optimized pools', 'Balance risk and return with our proprietary framework'],
    example: 'Through our Capital Acceleration strategy implementation, clients have achieved average returns of 36-42% annually on optimized liquidity pools.',
    metric: {
      value: '36%',
      label: 'Average annual return'
    },
    testimonial: {
      quote: 'This realtionship revolutionized how I think about liquidity and cash flow.',
      author: 'Sarah K., Business Owner'
    },
    variant: 'gold'
  }, {
    icon: <ShieldIcon className="w-8 h-8 text-gold-500" data-id="element-274" />,
    title: 'ASSET PROTECTION',
    description: 'Asset Protection Systems',
    benefits: ['Shield your wealth from potential legal and financial threats', 'Create structured protection for your family and business interests', 'Implement "Becoming Your Own Bank" strategies for greater control'],
    example: 'Our B.Y.O.B system has helped clients protect assets while maintaining liquidity and growth potential through strategic structuring.',
    metric: {
      value: '100%',
      label: 'Asset protection coverage'
    },
    testimonial: {
      quote: 'The peace of mind from knowing my assets are protected is invaluable.',
      author: 'Robert J., Serial Entrepreneur'
    },
    variant: 'gold'
  }, {
    icon: <BuildingIcon className="w-8 h-8 text-gold-500" data-id="element-275" />,
    title: 'ASSET ACQUISITION',
    description: 'Strategic Investments',
    benefits: ['Identify and acquire high-value assets with strategic advantage', "Leverage OPM (Other People's Money) for acquisitions", 'Build a diverse portfolio of cash-flowing assets'],
    example: 'Through our acquisition framework, clients have successfully acquired businesses and properties at 15-20% below market value.',
    metric: {
      value: '18%',
      label: 'Average below-market acquisition'
    },
    testimonial: {
      quote: 'The asset acquisition strategy helped me buy my third business with minimal personal capital.',
      author: 'Thomas L., Portfolio Entrepreneur'
    },
    variant: 'gold'
  }, {
    icon: <BarChartIcon className="w-8 h-8 text-gold-500" data-id="element-276" />,
    title: 'PROFIT PROTECTION',
    description: 'Wealth Accumulation',
    benefits: ['Implement advanced profit acceleration techniques', 'Optimize tax strategies for maximum wealth retention', 'Create compound growth systems that work while you sleep'],
    example: 'Our wealth accumulation framework has helped clients increase net worth by an average of 40% within the first 24 months.',
    metric: {
      value: '40%',
      label: 'Avg. net worth increase'
    },
    testimonial: {
      quote: "I've seen more profit growth in 18 months with Legacy Lab than in 5 years on my own.",
      author: 'David M., Healthcare Executive'
    },
    variant: 'gold'
  }, {
    icon: <GlobeIcon className="w-8 h-8 text-gold-500" data-id="element-277" />,
    title: 'EXPANSION & SUCCESSION',
    description: 'Global Growth Strategies',
    benefits: ['Scale your wealth across borders and markets', 'Diversify investments with international opportunities', 'Access global networks and partnerships'],
    example: 'Clients implementing our expansion strategies have successfully entered an average of 3 new markets within 18 months.',
    metric: {
      value: '3x',
      label: 'Average market reach increase'
    },
    testimonial: {
      quote: 'Legacy Lab helped me think beyond local markets and expand globally.',
      author: 'Jennifer P., Tech Founder'
    },
    variant: 'gold'
  }];
  const toggleCard = (index: number) => {
    if (expandedCard === index) {
      setExpandedCard(null);
    } else {
      setExpandedCard(index);
    }
  };
  return <section id="strategy" className="py-20 bg-gradient-to-b from-gold-50 to-white" data-id="element-278">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-279">
        <h2 className="text-4xl font-bold mb-4 section-title-decoration" data-id="element-280">
          Legacy Lab <span className="text-gradient" data-id="element-281">Strategy</span>
        </h2>
        <p className="max-w-3xl mx-auto text-lg text-gray-600 mb-16" data-id="element-282">
          Our comprehensive approach combines proven financial strategies with
          innovative wealth-building techniques, all designed to accelerate your
          path to financial freedom.
        </p>
        {/* First row - 3 cards */}
        <div className="relative mb-16" data-id="element-283">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10" data-id="element-284">
            {strategies.slice(0, 3).map((strategy, index) => <div key={index} className="strategy-card group" onClick={() => toggleCard(index)} data-id="element-285">
                {/* Card content */}
                <div className="flex justify-center mb-4" data-id="element-286">
                  <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300" data-id="element-287">
                    {strategy.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-288">
                  {strategy.title}
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-289">{strategy.description}</p>
                {/* Expand/collapse indicator */}
                <div className="flex items-center justify-center mt-2 text-gold-500" data-id="element-290">
                  <span className="text-sm mr-1" data-id="element-291">
                    {expandedCard === index ? 'Show less' : 'Learn more'}
                  </span>
                  {expandedCard === index ? <ChevronUpIcon className="w-4 h-4" data-id="element-292" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-293" />}
                </div>
                {/* Expanded content */}
                {expandedCard === index && <div className="mt-6 pt-6 border-t border-gray-100 text-left animate-fadeIn" data-id="element-294">
                    {/* Benefits */}
                    <h4 className="text-sm font-medium text-gold-600 mb-2" data-id="element-295">
                      Benefits
                    </h4>
                    <ul className="text-sm space-y-2 mb-4" data-id="element-296">
                      {strategy.benefits.map((benefit, i) => <li key={i} className="flex items-start" data-id="element-297">
                          <span className="text-gold-500 mr-2" data-id="element-298">•</span>
                          <span className="text-gray-700" data-id="element-299">{benefit}</span>
                        </li>)}
                    </ul>
                    {/* Example */}
                    <h4 className="text-sm font-medium text-gold-600 mb-2" data-id="element-300">
                      How It Works
                    </h4>
                    <p className="text-sm text-gray-700 mb-4" data-id="element-301">
                      {strategy.example}
                    </p>
                    {/* Success metric */}
                    <div className="bg-gold-50 p-3 rounded-lg text-center mb-4" data-id="element-302">
                      <span className="text-xl font-bold text-gold-600" data-id="element-303">
                        {strategy.metric.value}
                      </span>
                      <p className="text-xs text-gray-600" data-id="element-304">
                        {strategy.metric.label}
                      </p>
                    </div>
                    {/* Testimonial */}
                    <div className="border-t border-gray-100 pt-4" data-id="element-305">
                      <p className="text-sm italic text-gray-600" data-id="element-306">
                        "{strategy.testimonial.quote}"
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-307">
                        — {strategy.testimonial.author}
                      </p>
                    </div>
                  </div>}
              </div>)}
          </div>
          {/* Connector line between rows */}
          <div className="connector-line" data-id="element-308"></div>
        </div>
        {/* Second row - ASSET PROTECTION, ASSET ACQUISITION, PROFIT */}
        <div className="relative mb-16" data-id="element-309">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10" data-id="element-310">
            {strategies.slice(3, 6).map((strategy, index) => <div key={index + 3} className={strategy.variant === 'navy' ? 'strategy-card-navy group' : 'strategy-card group'} onClick={() => toggleCard(index + 3)} data-id="element-311">
                {/* Card content */}
                <div className="flex justify-center mb-4" data-id="element-312">
                  <div className={`w-16 h-16 ${strategy.variant === 'navy' ? 'bg-navy-100' : 'bg-gold-100'} rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300`} data-id="element-313">
                    {strategy.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-314">
                  {strategy.title}
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-315">{strategy.description}</p>
                {/* Expand/collapse indicator */}
                <div className="flex items-center justify-center mt-2 text-gold-500" data-id="element-316">
                  <span className="text-sm mr-1" data-id="element-317">
                    {expandedCard === index + 3 ? 'Show less' : 'Learn more'}
                  </span>
                  {expandedCard === index + 3 ? <ChevronUpIcon className="w-4 h-4" data-id="element-318" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-319" />}
                </div>
                {/* Expanded content */}
                {expandedCard === index + 3 && <div className="mt-6 pt-6 border-t border-gray-100 text-left animate-fadeIn" data-id="element-320">
                    {/* Benefits */}
                    <h4 className={`text-sm font-medium ${strategy.variant === 'navy' ? 'text-navy-600' : 'text-gold-600'} mb-2`} data-id="element-321">
                      Benefits
                    </h4>
                    <ul className="text-sm space-y-2 mb-4" data-id="element-322">
                      {strategy.benefits.map((benefit, i) => <li key={i} className="flex items-start" data-id="element-323">
                          <span className={strategy.variant === 'navy' ? 'text-navy-500 mr-2' : 'text-gold-500 mr-2'} data-id="element-324">
                            •
                          </span>
                          <span className="text-gray-700" data-id="element-325">{benefit}</span>
                        </li>)}
                    </ul>
                    {/* Example */}
                    <h4 className={`text-sm font-medium ${strategy.variant === 'navy' ? 'text-navy-600' : 'text-gold-600'} mb-2`} data-id="element-326">
                      How It Works
                    </h4>
                    <p className="text-sm text-gray-700 mb-4" data-id="element-327">
                      {strategy.example}
                    </p>
                    {/* Success metric */}
                    <div className={`${strategy.variant === 'navy' ? 'bg-navy-50' : 'bg-gold-50'} p-3 rounded-lg text-center mb-4`} data-id="element-328">
                      <span className={`text-xl font-bold ${strategy.variant === 'navy' ? 'text-navy-600' : 'text-gold-600'}`} data-id="element-329">
                        {strategy.metric.value}
                      </span>
                      <p className="text-xs text-gray-600" data-id="element-330">
                        {strategy.metric.label}
                      </p>
                    </div>
                    {/* Testimonial */}
                    <div className="border-t border-gray-100 pt-4" data-id="element-331">
                      <p className="text-sm italic text-gray-600" data-id="element-332">
                        "{strategy.testimonial.quote}"
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-333">
                        — {strategy.testimonial.author}
                      </p>
                    </div>
                  </div>}
              </div>)}
          </div>
          {/* Connector line between rows */}
          <div className="connector-line" data-id="element-334"></div>
        </div>
        {/* Third row - Just EXPANSION centered */}
        <div className="relative" data-id="element-335">
          <div className="flex justify-center relative z-10" data-id="element-336">
            <div className="w-full md:w-1/3" data-id="element-337">
              <div key={6} className="strategy-card group" onClick={() => toggleCard(6)} data-id="element-338">
                {/* Card content */}
                <div className="flex justify-center mb-4" data-id="element-339">
                  <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300" data-id="element-340">
                    {strategies[6].icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-341">
                  {strategies[6].title}
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-342">
                  {strategies[6].description}
                </p>
                {/* Expand/collapse indicator */}
                <div className="flex items-center justify-center mt-2 text-gold-500" data-id="element-343">
                  <span className="text-sm mr-1" data-id="element-344">
                    {expandedCard === 6 ? 'Show less' : 'Learn more'}
                  </span>
                  {expandedCard === 6 ? <ChevronUpIcon className="w-4 h-4" data-id="element-345" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-346" />}
                </div>
                {/* Expanded content */}
                {expandedCard === 6 && <div className="mt-6 pt-6 border-t border-gray-100 text-left animate-fadeIn" data-id="element-347">
                    {/* Benefits */}
                    <h4 className="text-sm font-medium text-gold-600 mb-2" data-id="element-348">
                      Benefits
                    </h4>
                    <ul className="text-sm space-y-2 mb-4" data-id="element-349">
                      {strategies[6].benefits.map((benefit, i) => <li key={i} className="flex items-start" data-id="element-350">
                          <span className="text-gold-500 mr-2" data-id="element-351">•</span>
                          <span className="text-gray-700" data-id="element-352">{benefit}</span>
                        </li>)}
                    </ul>
                    {/* Example */}
                    <h4 className="text-sm font-medium text-gold-600 mb-2" data-id="element-353">
                      How It Works
                    </h4>
                    <p className="text-sm text-gray-700 mb-4" data-id="element-354">
                      {strategies[6].example}
                    </p>
                    {/* Success metric */}
                    <div className="bg-gold-50 p-3 rounded-lg text-center mb-4" data-id="element-355">
                      <span className="text-xl font-bold text-gold-600" data-id="element-356">
                        {strategies[6].metric.value}
                      </span>
                      <p className="text-xs text-gray-600" data-id="element-357">
                        {strategies[6].metric.label}
                      </p>
                    </div>
                    {/* Testimonial */}
                    <div className="border-t border-gray-100 pt-4" data-id="element-358">
                      <p className="text-sm italic text-gray-600" data-id="element-359">
                        "{strategies[6].testimonial.quote}"
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-360">
                        — {strategies[6].testimonial.author}
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default StrategySection;